import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useFormField } from '../FormField/FormField';
import { Portal } from '../../utils/Portal';
import { useFloatingPosition } from '../../utils/useFloatingPosition';

/**
 * Combobox — input avec liste filtrée (search + select).
 *
 *   <Combobox
 *     options={[{ value: 'fr', label: 'France' }, ...]}
 *     value={selected}
 *     onChange={setSelected}
 *     placeholder="Choisir un pays…"
 *   />
 *
 * La listbox est rendue via Portal pour échapper aux overflow:hidden parents.
 */
export function Combobox({
  options = [],
  value,
  onChange,
  placeholder = 'Choisir…',
  emptyText = 'Aucun résultat',
  isClearable = true,
  isDisabled,
  isInvalid,
  size = 'md',
  className,
  id: idProp,
}) {
  const field = useFormField();
  const id = idProp || field?.id;
  const invalid = isInvalid || field?.invalid;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const triggerRef = useRef(null);
  const floatingRef = useRef(null);
  const inputRef = useRef(null);

  const { style } = useFloatingPosition({
    triggerRef,
    floatingRef,
    open,
    side: 'bottom',
    align: 'start',
    offset: 4,
    matchTriggerWidth: true,
  });

  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);

  const filtered = useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (floatingRef.current?.contains(e.target)) return;
      setOpen(false);
      setQuery('');
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const SIZES = {
    sm: 'h-8  text-sm   px-2.5',
    md: 'h-10 text-sm   px-3',
    lg: 'h-11 text-base px-3.5',
  };

  const handleSelect = (opt) => {
    onChange?.(opt.value);
    setOpen(false);
    setQuery('');
  };

  return (
    <div className={cn('relative w-full', className)}>
      <button
        ref={triggerRef}
        type="button"
        id={id}
        onClick={() => !isDisabled && setOpen(!open)}
        disabled={isDisabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        aria-describedby={field?.describedBy}
        className={cn(
          'w-full inline-flex items-center justify-between bg-surface text-text outline-none',
          'border border-border rounded-md transition-colors duration-fast',
          'focus:bg-surface focus:border-ink focus:ring-2 focus:ring-ink/15',
          'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-bg-2',
          SIZES[size],
          invalid && 'border-danger focus:border-danger focus:ring-danger/20',
        )}
      >
        <span className={cn(selected ? 'text-text' : 'text-mute-2', 'truncate')}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0 ml-2">
          {isClearable && selected && !isDisabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); onChange?.(null); }}
              className="text-mute hover:text-text p-0.5"
              aria-label="Effacer"
            >
              <X className="w-3.5 h-3.5" strokeWidth={2} />
            </span>
          )}
          <ChevronDown className={cn('w-4 h-4 text-mute transition-transform', open && 'rotate-180')} strokeWidth={1.8} />
        </span>
      </button>

      {open && (
        <Portal>
          <div
            ref={floatingRef}
            data-ds-theme="default"
            style={style}
            className="z-overlay bg-surface border border-border rounded-md shadow-md animate-scale-in origin-top"
          >
            <div className="relative border-b border-border">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-mute" strokeWidth={1.8} />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filtrer…"
                className="w-full pl-9 pr-3 h-9 bg-transparent text-sm outline-none placeholder:text-mute-2"
              />
            </div>
            <ul role="listbox" className="max-h-60 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-mute italic text-center">{emptyText}</li>
              ) : (
                filtered.map((opt) => {
                  const isSelected = opt.value === value;
                  return (
                    <li
                      key={opt.value}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => handleSelect(opt)}
                      className={cn(
                        'flex items-center gap-2 px-2.5 py-1.5 text-sm cursor-pointer transition-colors',
                        isSelected ? 'bg-bg-2 font-medium' : 'hover:bg-bg-2/60',
                      )}
                    >
                      <span className="w-4 h-4 inline-flex items-center justify-center">
                        {isSelected && <Check className="w-3.5 h-3.5 text-ink" strokeWidth={2.5} />}
                      </span>
                      <span className="flex-1">{opt.label}</span>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
        </Portal>
      )}
    </div>
  );
}

export default Combobox;
